import React from 'react';

import { Grid, TextField } from '@mui/material';
import { useFormContext } from 'react-hook-form';

import { Stack } from '@liscio/ui';

import { CreateAccountPrefTypeOptions, CreateAccountSectionProps } from './sectionTypes';
import { LabeledField, SubsectionTitle } from '../CreateAccountFormFieldPrimitives';
import { CreateAccountFormValues } from '../createAccountValidationSchema';
import { InlineRichOptionSelect } from '../InlineRichOptionSelect';
import { stateOptionsAbbreviated } from 'components/Select/option-sets';
import { useHomeData } from 'fetch-utils/users/user-hooks';



export interface CreateAccountTaxpayerDetailsFieldsProps
  extends CreateAccountSectionProps {
  prefTypeOptions?: CreateAccountPrefTypeOptions;
}

export const CreateAccountTaxpayerDetailsFields: React.FC<
  CreateAccountTaxpayerDetailsFieldsProps
> = ({ layout = 'responsive', prefTypeOptions: prefTypeOptionsProp }) => {
  const { data: homeData } = useHomeData();
  const {
    register,
    control,
    formState: { errors },
  } = useFormContext<CreateAccountFormValues>();

  const prefTypeOptions =
    prefTypeOptionsProp ?? homeData?.data?.pref_type_options;
  const filingStatusOptions = prefTypeOptions?.filing_status;
  const firmType = (homeData?.data as { firm_type?: string } | undefined)
    ?.firm_type;
  const notCpa = firmType && firmType !== 'cpa';
  const namePrefix = notCpa ? 'Matter ' : '';

  const nameBreakpoint = layout === 'single-column' ? 12 : 4;
  const licenseBreakpoint = layout === 'single-column' ? 12 : 3;

  return (
    <Stack spacing={2}>
      <SubsectionTitle>
        {notCpa ? 'Matter Details' : 'Taxpayer Details'}
      </SubsectionTitle>
      <Grid container spacing={2.5} width="100%" sx={{ m: 0 }}>
        <Grid item xs={12} sm={nameBreakpoint}>
          <LabeledField label={`${namePrefix}First Name`}>
            <TextField
              {...register('taxpayer_first_name')}
              fullWidth
              error={Boolean(errors.taxpayer_first_name)}
              helperText={errors.taxpayer_first_name?.message}
              autoComplete="off"
            />
          </LabeledField>
        </Grid>

        <Grid item xs={12} sm={nameBreakpoint}>
          <LabeledField label={`${namePrefix}Middle Name`}>
            <TextField
              {...register('taxpayer_middle_name')}
              fullWidth
              error={Boolean(errors.taxpayer_middle_name)}
              helperText={errors.taxpayer_middle_name?.message}
              autoComplete="off"
            />
          </LabeledField>
        </Grid>

        <Grid item xs={12} sm={nameBreakpoint}>
          <LabeledField label={`${namePrefix}Last Name`}>
            <TextField
              {...register('taxpayer_last_name')}
              fullWidth
              error={Boolean(errors.taxpayer_last_name)}
              helperText={errors.taxpayer_last_name?.message}
              autoComplete="off"
            />
          </LabeledField>
        </Grid>

        {filingStatusOptions && (
          <Grid item xs={12} sm={nameBreakpoint}>
            <LabeledField label="Current filing status?">
              <InlineRichOptionSelect
                name="in_curr_filling_status"
                control={control}
                data={filingStatusOptions}
                error={errors.in_curr_filling_status}
                placeholder="Select..."
              />
            </LabeledField>
          </Grid>
        )}

        <Grid item xs={12} sm={nameBreakpoint}>
          <LabeledField label="Taxpayer ID">
            <TextField
              {...register('encryptedInSelfSsn')}
              fullWidth
              type="password"
              error={Boolean(errors.encryptedInSelfSsn)}
              helperText={errors.encryptedInSelfSsn?.message}
              autoComplete="off"
            />
          </LabeledField>
        </Grid>

        <Grid item xs={12} sm={nameBreakpoint}>
          <LabeledField label="Birthdate">
            <TextField
              {...register('in_self_birthdate')}
              fullWidth
              type="date"
              InputLabelProps={{ shrink: true }}
              error={Boolean(errors.in_self_birthdate)}
              helperText={errors.in_self_birthdate?.message}
            />
          </LabeledField>
        </Grid>

        <Grid item xs={12} sm={licenseBreakpoint}>
          <LabeledField label="Drivers License #">
            <TextField
              {...register('in_self_driver_license')}
              fullWidth
              error={Boolean(errors.in_self_driver_license)}
              helperText={errors.in_self_driver_license?.message}
              autoComplete="off"
            />
          </LabeledField>
        </Grid>

        <Grid item xs={12} sm={licenseBreakpoint}>
          <LabeledField label="Date Issued">
            <TextField
              {...register('in_self_license_issue_date')}
              fullWidth
              type="date"
              InputLabelProps={{ shrink: true }}
              error={Boolean(errors.in_self_license_issue_date)}
              helperText={errors.in_self_license_issue_date?.message}
            />
          </LabeledField>
        </Grid>

        <Grid item xs={12} sm={licenseBreakpoint}>
          <LabeledField label="Date Expired">
            <TextField
              {...register('in_self_license_expiry_date')}
              fullWidth
              type="date"
              InputLabelProps={{ shrink: true }}
              error={Boolean(errors.in_self_license_expiry_date)}
              helperText={errors.in_self_license_expiry_date?.message}
            />
          </LabeledField>
        </Grid>

        <Grid item xs={12} sm={licenseBreakpoint}>
          <LabeledField label="State of issue">
            <InlineRichOptionSelect
              name="in_self_license_state"
              control={control}
              data={stateOptionsAbbreviated}
              error={errors.in_self_license_state}
              placeholder="Select..."
            />
          </LabeledField>
        </Grid>
      </Grid>
    </Stack>
  );
};

export default CreateAccountTaxpayerDetailsFields;
